import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { getAccessToken, refreshAccessToken, endSessionIfSuspended } from './refresh';
import { IS_BFF, applyBffAuth } from './auth-mode';

/**
 * User-context HTTP client for the standalone Python `data-service`.
 *
 * Used by the process-flow (agentflow) + execution modules, which are served
 * by the data-service exactly as in Wexa-web. In the browser, requests go to
 * `/data-api/*` which `next.config.js` rewrites to NEXT_PUBLIC_DATA_SERVICE_URL.
 *
 * IMPORTANT — auth differs from tables-client.ts on purpose:
 *   - tables-client sends the `stsk` server-key, which the data-service treats
 *     as a *trusted-backend bypass* → AuthenticatedUser(organization=None).
 *   - Agentflow endpoints (create flow, copy, execute) REQUIRE the caller's
 *     real organization (they 404 with "Organization not found" otherwise).
 * So this client sends ONLY the user's Bearer token — identical to how
 * Wexa-web calls the data-service — so `check_authorization` resolves the
 * real user + organization from the identity token.
 */

const isBrowser = typeof window !== 'undefined';
const DATA_API_BASE_URL = isBrowser
  ? '/data-api'
  : process.env.NEXT_PUBLIC_DATA_SERVICE_URL || 'http://localhost:4000';

class DataServiceClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: DATA_API_BASE_URL,
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    });

    this.client.interceptors.request.use(
      async (config) => {
        // bff mode: no JS token; the BFF injects Authorization from the httpOnly
        // cookie. Send cookies + CSRF (on mutations) only.
        if (IS_BFF) return applyBffAuth(config);
        // Freshest non-expired copy across cookie + localStorage (see refresh.ts).
        const token = getAccessToken();
        if (!token) {
          return Promise.reject(new Error('No authentication token'));
        }
        // Bearer ONLY — no stsk/x-server-key, so the data-service resolves the
        // real user + organization (required by agentflow/execute endpoints).
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      },
      (error) => Promise.reject(error),
    );

    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
        if (error.response?.status === 401 && original && !original._retry) {
          original._retry = true;
          const newToken = await refreshAccessToken();
          if (newToken) {
            // Retry once with the refreshed token (request interceptor re-reads it).
            return this.client(original);
          }
          console.warn('[DataService Client] 401 and refresh failed:', original?.url);
        }
        // FAB-303: a suspended member must be signed out, not left
        // seeing bare errors on actions the UI still offers.
        endSessionIfSuspended(error);
        return Promise.reject(error);
      },
    );
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const res = await this.client.get<T>(url, config);
    return res.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const res = await this.client.post<T>(url, data, config);
    return res.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const res = await this.client.put<T>(url, data, config);
    return res.data;
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const res = await this.client.patch<T>(url, data, config);
    return res.data;
  }

  async delete<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const res = await this.client.delete<T>(url, { ...config, data });
    return res.data;
  }
}

export const dataServiceClient = new DataServiceClient();
